/*
 * AdminTimeline — unified per-user activity feed for the admin portal.
 *
 * Sprint 2: interleaves orders, claims, returns, invoices, wallet txns,
 * notifications, score snapshots, audit log, and Shopify install events
 * into a single chronological stream. Filterable by event type so admins
 * can answer questions like "show me everything Apex did in the last 24h"
 * in one screen.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, RotateCcw, Receipt, Wallet, Bell, TrendingUp,
  ShieldCheck, Plug, Package, Loader2, Search, Filter as FilterIcon,
} from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { PageHeader } from "../../components/PortalShell";

const TYPE_META = {
  order:        { label: "Order",        icon: Package,        tone: "indigo" },
  claim:        { label: "Claim",        icon: AlertTriangle,  tone: "rose" },
  return:       { label: "Return",       icon: RotateCcw,      tone: "amber" },
  invoice:      { label: "Invoice",      icon: Receipt,        tone: "blue" },
  wallet:       { label: "Wallet",       icon: Wallet,         tone: "emerald" },
  notification: { label: "Notification", icon: Bell,           tone: "neutral" },
  score:        { label: "Score",        icon: TrendingUp,     tone: "violet" },
  audit:        { label: "Audit",        icon: ShieldCheck,    tone: "amber" },
  shopify:      { label: "Shopify",      icon: Plug,           tone: "emerald" },
};
const TONE_BG = {
  indigo:  "bg-indigo-100 text-indigo-700 border-indigo-200",
  rose:    "bg-rose-100 text-rose-700 border-rose-200",
  amber:   "bg-amber-100 text-amber-800 border-amber-200",
  blue:    "bg-blue-100 text-blue-800 border-blue-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  neutral: "bg-foreground/8 text-foreground/65 border-foreground/10",
  violet:  "bg-violet-100 text-violet-700 border-violet-200",
};

export default function AdminTimeline() {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    Promise.all([
      api.get("/admin/buyers").then(r => r.data),
      api.get("/admin/sellers").then(r => r.data),
    ]).then(([b, s]) => setUsers([...b, ...s]));
  }, []);

  const filteredUsers = useMemo(() => {
    if (!query.trim()) return users.slice(0, 30);
    const q = query.trim().toLowerCase();
    return users.filter(u =>
      (u.company || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q),
    ).slice(0, 30);
  }, [users, query]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/admin/timeline/${userId}?limit=300`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const filteredEvents = useMemo(() => {
    if (!data?.events) return [];
    if (typeFilter === "all") return data.events;
    return data.events.filter(e => e.type === typeFilter);
  }, [data, typeFilter]);

  return (
    <div>
      <PageHeader title="Per-user activity timeline"
        subtitle="One feed across every event a user produces — orders, claims, returns, invoices, wallet, scores, Shopify, notifications. Sprint 2 of admin observability."/>
      <div className="mt-6 grid lg:grid-cols-[320px_1fr] gap-5">
        {/* Left rail — user picker */}
        <div className="rounded-2xl border border-border bg-white">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
              <Input value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Search by name · email · company"
                className="pl-9 h-9"/>
            </div>
          </div>
          <div className="max-h-[640px] overflow-y-auto divide-y divide-border">
            {filteredUsers.map(u => (
              <button key={u.id} onClick={() => setUserId(u.id)}
                data-testid={`admin-timeline-user-${u.id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary/40 ${
                  userId === u.id ? "bg-foreground/5" : ""
                }`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium truncate">{u.company || u.name}</div>
                  <div className="text-[10.5px] text-foreground/55 truncate">{u.email} · {u.role}</div>
                </div>
              </button>
            ))}
            {filteredUsers.length === 0 && <div className="p-6 text-center text-foreground/45 text-[12px]">No users match.</div>}
          </div>
        </div>

        {/* Right pane — timeline */}
        <div>
          {!userId ? (
            <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-12 text-center text-foreground/55 text-[13px]">
              Pick a user to view their unified timeline.
            </div>
          ) : loading ? (
            <div className="rounded-2xl border border-border bg-white p-12 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-foreground/40"/>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-2xl border border-border bg-white p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="display text-xl">{data?.user?.company || data?.user?.name}</div>
                    <div className="text-[11.5px] text-foreground/55 mt-0.5">
                      {data?.user?.email} · {data?.user?.role} · {data?.total} total events
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => setTypeFilter("all")}
                      data-testid="admin-timeline-filter-all"
                      className={`rounded-full h-7 px-3 text-[11px] mono ${typeFilter === "all" ? "bg-foreground text-background" : "bg-white border border-border hover:bg-secondary"}`}>All</button>
                    {Object.keys(TYPE_META).map(k => (
                      <button key={k} onClick={() => setTypeFilter(k)}
                        data-testid={`admin-timeline-filter-${k}`}
                        className={`rounded-full h-7 px-3 text-[11px] mono inline-flex items-center gap-1 ${typeFilter === k ? "bg-foreground text-background" : "bg-white border border-border hover:bg-secondary"}`}>
                        {TYPE_META[k].label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-white max-h-[680px] overflow-y-auto">
                {filteredEvents.length === 0 ? (
                  <div className="p-12 text-center text-foreground/55 text-[13px]">No events match filter.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredEvents.map((e, i) => {
                      const meta = TYPE_META[e.type] || TYPE_META.notification;
                      return (
                        <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-secondary/30">
                          <div className={`shrink-0 w-7 h-7 rounded-lg grid place-items-center border ${TONE_BG[meta.tone]}`}>
                            <meta.icon className="w-3.5 h-3.5"/>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-[13px] font-medium truncate">{e.title}</div>
                              <span className="text-[9.5px] mono uppercase tracking-[0.14em] text-foreground/45">{meta.label}</span>
                            </div>
                            {e.detail && <div className="text-[11.5px] text-foreground/55 mt-0.5 truncate">{e.detail}</div>}
                          </div>
                          <div className="shrink-0 text-[10.5px] mono text-foreground/45 text-right">
                            {e.ts ? new Date(e.ts).toLocaleString() : "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
